// The SOS alarm (sos.alarm.ts decides; this makes the noise). Two channels:
//
//   SOUND — a two-tone beep, every few seconds, while any open SOS is still
//   unacknowledged. Generated with Web Audio, so there is no sound file to
//   ship. Browsers only let a page make sound after the person has interacted
//   with it; an operator always has (signing in is a click), and the audio is
//   unlocked again on the next click or key if a browser suspended it.
//
//   BROWSER NOTIFICATION — once per new SOS, so a backgrounded tab or a
//   minimised window still surfaces it. Permission is asked on the operator's
//   first click on the console, never with a pop-up out of nowhere.
//
// Mounted once per screen: the live map passes its own alerts (real or
// simulated); every other page gets the real ones through SosAlerts.

import { useEffect, useRef } from "react";
import {
  hasUnacknowledged,
  newUnacknowledged,
  SOS_ALARM_EVERY_MS,
  sosNotificationText,
} from "./sos.alarm";
import type { SosAlertView } from "./sos.model";

/** One page-wide audio context, created on first use and reused. */
let sharedContext: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (typeof window === "undefined" || !("AudioContext" in window)) return null;
  sharedContext ??= new AudioContext();
  return sharedContext;
}

/** Two short tones, high then low — urgent without being a siren. */
function beep() {
  const context = audioContext();
  if (!context || context.state !== "running") return;
  const start = context.currentTime;
  [
    [880, 0],
    [660, 0.22],
    [880, 0.44],
    [660, 0.66],
  ].forEach(([frequency, offset]) => {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = frequency!;
    gain.gain.setValueAtTime(0.0001, start + offset!);
    gain.gain.exponentialRampToValueAtTime(0.25, start + offset! + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + offset! + 0.2);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start + offset!);
    oscillator.stop(start + offset! + 0.21);
  });
}

export function useSosAlarm(alerts: readonly SosAlertView[] | undefined) {
  const ringing = hasUnacknowledged(alerts ?? []);
  const announced = useRef(new Set<string>());

  // Unlock audio, and ask for notification permission, on the first gesture.
  useEffect(() => {
    const unlock = () => {
      void audioContext()?.resume();
      if ("Notification" in window && Notification.permission === "default") {
        void Notification.requestPermission();
      }
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Ring now, then every few seconds, until nobody is waiting.
  useEffect(() => {
    if (!ringing) return;
    beep();
    const timer = window.setInterval(beep, SOS_ALARM_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [ringing]);

  // One notification per new SOS.
  useEffect(() => {
    if (!alerts) return;
    for (const alert of newUnacknowledged(alerts, announced.current)) {
      announced.current.add(alert.id);
      if (!("Notification" in window) || Notification.permission !== "granted") continue;
      const { title, body } = sosNotificationText(alert);
      const notification = new Notification(title, {
        body,
        tag: `sos:${alert.id}`,
        // The Bednbody mark, so the alarm is recognisable in the system tray.
        icon: "/icons/icon-192.png",
      });
      notification.onclick = () => window.focus();
    }
  }, [alerts]);
}
